﻿import type { DependencyList, RefObject } from 'react';
import { useEffect, useRef } from 'react';

type Entry<T extends HTMLElement> = Exclude<ResizeObserverEntry, 'target'> & { target: T };
export function useResizeObserver<T extends HTMLElement>(
  onResize: (entries: Entry<T>[]) => void,
  deps: DependencyList,
): RefObject<T | null> {
  const ref = useRef<T>(null);
  const observerRef = useRef(new ResizeObserver(onResize as (entry: ResizeObserverEntry[]) => void));
  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to rerun this effect when deps change
  useEffect(() => {
    const currentObserver = observerRef.current;
    const currentRef = ref.current;
    if (currentRef) {
      currentObserver.observe((ref as RefObject<T>).current);
    }
    return () => {
      if (currentRef) {
        currentObserver.unobserve(currentRef);
      }
    };
  }, [deps]);
  return ref;
}
